/**
 * Webcam Service
 * Handles camera access, photo capture, and image processing for check-ins
 */

// Constants
const DEFAULT_CONSTRAINTS = {
  video: {
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    facingMode: 'user' // Front camera
  },
  audio: false
};

const PHOTO_QUALITY = 0.85;
const MAX_PHOTO_WIDTH = 1280;
const MAX_PHOTO_HEIGHT = 720;

/**
 * Check if the browser supports webcam access
 * @returns {boolean}
 */
export const isWebcamSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * Request camera permission and get media stream
 * @param {Object} constraints - Optional MediaStreamConstraints
 * @returns {Promise<{stream: MediaStream|null, error: Error|null}>}
 */
export const requestCameraPermission = async (constraints = DEFAULT_CONSTRAINTS) => {
  try {
    if (!isWebcamSupported()) {
      return { 
        stream: null, 
        error: new Error('Your browser does not support camera access. Please use a modern browser.') 
      };
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return { stream, error: null };
  } catch (error) {
    console.error('Error requesting camera permission:', error);
    
    let userFriendlyError;
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        userFriendlyError = new Error('Camera access was denied. Please allow camera access in your browser settings.');
        break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        userFriendlyError = new Error('No camera found. Please connect a webcam and try again.');
        break;
      case 'NotReadableError':
      case 'TrackStartError':
        userFriendlyError = new Error('Camera is already in use by another application. Please close other apps using the camera.');
        break;
      case 'OverconstrainedError':
        userFriendlyError = new Error('Camera does not support the requested resolution. Trying with default settings.');
        break;
      default:
        userFriendlyError = new Error(`Camera error: ${error.message}`);
    }
    
    return { stream: null, error: userFriendlyError };
  }
};

/**
 * Start camera and attach to video element
 * @param {HTMLVideoElement} videoElement - Video element to attach stream to
 * @param {Object} constraints - Optional MediaStreamConstraints
 * @returns {Promise<{stream: MediaStream|null, error: Error|null}>}
 */
export const startCamera = async (videoElement, constraints = DEFAULT_CONSTRAINTS) => {
  try {
    const { stream, error } = await requestCameraPermission(constraints);
    
    if (error) {
      // Try with minimal constraints if initial request fails
      if (error.message.includes('resolution')) {
        const minimalConstraints = { video: true, audio: false };
        const { stream: fallbackStream, error: fallbackError } = await requestCameraPermission(minimalConstraints);
        
        if (fallbackError) {
          return { stream: null, error: fallbackError };
        }
        
        videoElement.srcObject = fallbackStream;
        await videoElement.play();
        return { stream: fallbackStream, error: null };
      }
      return { stream: null, error };
    }

    videoElement.srcObject = stream;
    await videoElement.play();
    
    return { stream, error: null };
  } catch (error) {
    console.error('Error starting camera:', error);
    return { stream: null, error };
  }
};

/**
 * Stop camera and release resources
 * @param {MediaStream} stream - MediaStream to stop
 */
export const stopCamera = (stream) => {
  if (stream) {
    stream.getTracks().forEach(track => {
      track.stop();
    });
  }
};

/**
 * Capture a photo from video stream
 * @param {HTMLVideoElement} videoElement - Video element with active stream
 * @param {Object} options - Capture options
 * @param {boolean} options.mirror - Mirror the image horizontally (default: true)
 * @returns {{dataUrl: string|null, blob: Blob|null, error: Error|null}}
 */
export const capturePhoto = async (videoElement, options = {}) => {
  try {
    const { mirror = true, quality = PHOTO_QUALITY } = options;

    if (!videoElement || !videoElement.srcObject) {
      return { dataUrl: null, blob: null, error: new Error('No active video stream') };
    }

    // Create canvas with video dimensions
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Get actual video dimensions
    const videoWidth = videoElement.videoWidth || 640;
    const videoHeight = videoElement.videoHeight || 480;

    // Calculate dimensions maintaining aspect ratio
    let width = videoWidth;
    let height = videoHeight;

    if (width > MAX_PHOTO_WIDTH) {
      const ratio = MAX_PHOTO_WIDTH / width;
      width = MAX_PHOTO_WIDTH;
      height = Math.round(height * ratio);
    }

    if (height > MAX_PHOTO_HEIGHT) {
      const ratio = MAX_PHOTO_HEIGHT / height;
      height = MAX_PHOTO_HEIGHT;
      width = Math.round(width * ratio);
    }

    canvas.width = width;
    canvas.height = height;

    // Mirror image if front-facing camera
    if (mirror) {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }

    // Draw video frame to canvas
    ctx.drawImage(videoElement, 0, 0, width, height);

    // Add timestamp overlay
    ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
    addTimestampOverlay(ctx, width, height);

    // Convert to data URL and blob
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const blob = await dataUrlToBlob(dataUrl);

    return { dataUrl, blob, error: null };
  } catch (error) {
    console.error('Error capturing photo:', error);
    return { dataUrl: null, blob: null, error };
  }
};

/**
 * Add timestamp overlay to canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
const addTimestampOverlay = (ctx, width, height) => {
  const timestamp = new Date().toLocaleString();
  
  // Semi-transparent background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(0, height - 30, width, 30);
  
  // Timestamp text
  ctx.fillStyle = 'white';
  ctx.font = '14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(timestamp, width / 2, height - 10);
};

/**
 * Convert data URL to Blob without using fetch (avoids CSP connect-src restrictions)
 * @param {string} dataUrl - Data URL string
 * @returns {Promise<Blob>}
 */
const dataUrlToBlob = async (dataUrl) => {
  const [header, base64Data] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

/**
 * Compress an image blob
 * @param {Blob} blob - Original image blob
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Max width
 * @param {number} options.maxHeight - Max height
 * @param {number} options.quality - JPEG quality (0-1)
 * @returns {Promise<{blob: Blob|null, error: Error|null}>}
 */
export const compressPhoto = async (blob, options = {}) => {
  try {
    const { maxWidth = MAX_PHOTO_WIDTH, maxHeight = MAX_PHOTO_HEIGHT, quality = PHOTO_QUALITY } = options;

    return new Promise((resolve) => {
      // eslint-disable-next-line no-undef
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = Math.round(height * ratio);
        }

        if (height > maxHeight) {
          const ratio = maxHeight / height;
          height = maxHeight;
          width = Math.round(width * ratio);
        }

        // Create canvas and compress
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (compressedBlob) => {
            resolve({ blob: compressedBlob, error: null });
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        resolve({ blob: null, error: new Error('Failed to load image for compression') });
      };

      img.src = URL.createObjectURL(blob);
    });
  } catch (error) {
    console.error('Error compressing photo:', error);
    return { blob: null, error };
  }
};

/**
 * Get available cameras
 * @returns {Promise<{devices: Array|null, error: Error|null}>}
 */
export const getAvailableCameras = async () => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return { devices: null, error: new Error('Camera enumeration not supported') };
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter(device => device.kind === 'videoinput');

    return { 
      devices: cameras.map(camera => ({
        deviceId: camera.deviceId,
        label: camera.label || `Camera ${cameras.indexOf(camera) + 1}`
      })), 
      error: null 
    };
  } catch (error) {
    console.error('Error getting available cameras:', error);
    return { devices: null, error };
  }
};

/**
 * Switch to a different camera
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {string} deviceId - Device ID of camera to switch to
 * @returns {Promise<{stream: MediaStream|null, error: Error|null}>}
 */
export const switchCamera = async (videoElement, deviceId) => {
  try {
    // Stop current stream
    if (videoElement.srcObject) {
      stopCamera(videoElement.srcObject);
    }

    const constraints = {
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      },
      audio: false
    };

    return await startCamera(videoElement, constraints);
  } catch (error) {
    console.error('Error switching camera:', error);
    return { stream: null, error };
  }
};

/**
 * Check camera permission status
 * @returns {Promise<string>} 'granted', 'denied', or 'prompt'
 */
export const checkCameraPermission = async () => {
  try {
    if (!navigator.permissions) {
      return 'prompt'; // Can't determine, assume prompt
    }

    const permission = await navigator.permissions.query({ name: 'camera' });
    return permission.state;
  } catch (error) {
    console.error('Error checking camera permission:', error);
    return 'prompt';
  }
};

/**
 * Create a countdown before capture
 * @param {number} seconds - Countdown duration
 * @param {Function} onTick - Callback for each tick (receives remaining seconds)
 * @returns {Promise<void>}
 */
export const countdownCapture = (seconds, onTick) => {
  return new Promise((resolve) => {
    let remaining = seconds;
    
    const interval = setInterval(() => {
      if (remaining <= 0) {
        clearInterval(interval);
        resolve();
      } else {
        onTick(remaining);
        remaining--;
      }
    }, 1000);
  });
};

export default {
  isWebcamSupported,
  requestCameraPermission,
  startCamera,
  stopCamera,
  capturePhoto,
  compressPhoto,
  getAvailableCameras,
  switchCamera,
  checkCameraPermission,
  countdownCapture,
  DEFAULT_CONSTRAINTS,
  PHOTO_QUALITY,
  MAX_PHOTO_WIDTH,
  MAX_PHOTO_HEIGHT
};
